import type { RecordPoint, ViewBlockAbstract } from '@lighthouse/core'
import { BlockType, type DataSourceAbstract, DataSourceType } from '@lighthouse/core'
import { type RecordPoolMap, VIEW_BLOCK_NAME_MAP } from '@lighthouse/shared'
import { atomWithImmer } from 'jotai-immer'
import type { FC } from 'react'
import React, { createContext, useContext, useMemo } from 'react'

export type ViewBlockContextType = {
    uniqueId: string
    blockData: ViewBlockAbstract
    dataSource: DataSourceAbstract
    dataSourceList: DataSourceAbstract[]
    recordPoints: RecordPoint[]
    /** recordPoolAtom 使用 Map 结构，key 为 `${dsId}-${recordId}` */
    recordPoolAtom: ReturnType<typeof atomWithImmer<RecordPoolMap>>
}
const initDataSource: DataSourceAbstract = {
    id: '',
    appId: '',
    appName: '',
    envId: '',
    name: '',
    type: DataSourceType.dataSource,
    sync: false,
    createdBy: '',
    viewOptions: {
        tableProps: [],
        pagination: {
            currentPage: 1
        }
    },
    schema: {}
}

const initViewBlock: ViewBlockAbstract = {
    id: '',
    type: BlockType.view,
    title: VIEW_BLOCK_NAME_MAP['table'],
    config: {
        pointer: '',
        showTitle: false,
        viewFieldSettings: [],
        toolbar: [],
        recordOperator: [],
        eventList: [],
        pagination: {
            pageSize: 10
        },
        quickFilter: { mode: 'normal', fieldIds: [] },
        viewType: 'table',
        appId: '',
        breakPoint: {
            id: '',
            size: {
                width: {
                    size: 'fill'
                },
                height: {
                    size: 'auto'
                },
                overflow: 'auto'
            }
        },
        breakPoints: []
    },
    children: []
}

const ViewBlockContext = createContext<ViewBlockContextType>({
    uniqueId: '',
    blockData: initViewBlock,
    dataSource: initDataSource,
    dataSourceList: [],
    recordPoints: [],
    recordPoolAtom: atomWithImmer<RecordPoolMap>(new Map())
})

type ContainerBlockProviderProps = ViewBlockContextType & {
    children?: React.ReactNode
}

export const ViewBlockProvider: FC<ContainerBlockProviderProps> = ({
    uniqueId,
    blockData,
    dataSource,
    dataSourceList,
    recordPoints,
    recordPoolAtom,
    children
}) => {
    const value = useMemo(
        () => ({
            uniqueId,
            blockData,
            dataSource,
            dataSourceList,
            recordPoints,
            recordPoolAtom
        }),
        [blockData, dataSource, dataSourceList, recordPoints, recordPoolAtom, uniqueId]
    )
    return <ViewBlockContext.Provider value={value}>{children}</ViewBlockContext.Provider>
}

export const useViewBlockContext = () => useContext(ViewBlockContext)
